import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Search, Upload, Users, UserPlus, FileText, Download, Eye, MoreVertical, Info, Trash2, CheckCircle, XCircle, Edit, Phone, Mail, MapPin, Calendar, User, GraduationCap, CreditCard, UserX, UserCheck, Receipt, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import BulkImportDialog from '@/components/BulkImportDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentsPage = () => {
  const { getAuthHeader, user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  
  // Bulk Class Assignment state
  const [assignClassOpen, setAssignClassOpen] = useState(false);
  const [studentsWithoutClass, setStudentsWithoutClass] = useState([]);
  const [selectedClassForAssign, setSelectedClassForAssign] = useState('');
  const [selectedSectionForAssign, setSelectedSectionForAssign] = useState('');
  const [assigningClass, setAssigningClass] = useState(false);
  
  // Documents viewer state
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDocuments, setStudentDocuments] = useState([]);
  const [missingDocuments, setMissingDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState({});
  
  // View Details dialog state
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [studentPhotoUrl, setStudentPhotoUrl] = useState(null);
  
  // Status change dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusStudent, setStatusStudent] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);

  // Fetch student photo
  const fetchStudentPhoto = async (studentId) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/student/${studentId}`, { headers: getAuthHeader() });
      const docs = res.data.documents || [];
      // Get latest photo (sort by uploaded_at descending)
      const photos = docs.filter(d => d.document_type === 'student_photo')
        .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
      const photoDoc = photos[0];
      if (photoDoc?.cloudinary_url) {
        setStudentPhotoUrl(photoDoc.cloudinary_url);
      } else if (photoDoc?.file_path && photoDoc.file_path.startsWith('http')) {
        setStudentPhotoUrl(photoDoc.file_path);
      } else {
        setStudentPhotoUrl(null);
      }
    } catch (error) {
      console.error('Failed to fetch photo:', error);
      setStudentPhotoUrl(null);
    }
  };

  const openStudentViewDetails = (student) => {
    // Enrich student data with class/section names
    const enrichedStudent = {
      ...student,
      class_name: getClassName(student.class_id),
      section_name: getSectionName(student.section_id)
    };
    setViewingStudent(enrichedStudent);
    setStudentPhotoUrl(null);
    setViewDetailsOpen(true);
    fetchStudentPhoto(student.id);
  };

  // Check if user is admin (can add/edit students)
  const isAdmin = user?.role === 'school_admin' || user?.role === 'super_admin';
  const isTeacher = user?.role === 'teacher';
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    fetchData(1, classFilter, sectionFilter);
  }, []);

  // Re-fetch when class or section filter changes
  useEffect(() => {
    fetchData(1, classFilter, sectionFilter);
  }, [classFilter]);

  const fetchData = async (page = 1, classId = null, sectionId = null) => {
    try {
      // Fetch with pagination and filters
      let studentsData = [];
      let total = 0;
      let paginationData = null;
      
      try {
        // Build query params with class filter
        let apiUrl = `${API_URL}/api/students?page=${page}&page_size=${pageSize}`;
        if (classId && classId !== 'all') {
          apiUrl += `&class_id=${classId}`;
        }
        
        const studentsRes = await axios.get(apiUrl, { headers: getAuthHeader() });
        // Handle paginated response
        if (Array.isArray(studentsRes.data)) {
          studentsData = studentsRes.data;
          total = studentsRes.data.length;
        } else {
          studentsData = studentsRes.data.items || [];
          total = studentsRes.data.total || studentsData.length;
          paginationData = {
            page: studentsRes.data.page,
            total: studentsRes.data.total,
            total_pages: studentsRes.data.total_pages,
            has_next: studentsRes.data.has_next,
            has_prev: studentsRes.data.has_prev
          };
        }
      } catch (studentErr) {
        console.warn('Primary students API failed, using fallback');
        const fallbackRes = await axios.get(`${API_URL}/api/behavior/students-list`, { headers: getAuthHeader() });
        studentsData = Array.isArray(fallbackRes.data) ? fallbackRes.data : (fallbackRes.data.items || []);
        total = studentsData.length;
      }
      
      const [classesRes, sectionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/sections`, { headers: getAuthHeader() })
      ]);
      setStudents(studentsData);
      setTotalStudents(total);
      setPagination(paginationData);
      setCurrentPage(page);
      setClasses(classesRes.data);
      setSections(sectionsRes.data);
      
      // Count students without class from current page
      const noClassStudents = studentsData.filter(s => !s.class_id);
      setStudentsWithoutClass(noClassStudents);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load students');
    }
  };

  // Bulk assign class to students without class
  const handleBulkAssignClass = async () => {
    if (!selectedClassForAssign) {
      toast.error('Please select a class');
      return;
    }
    
    const selectedClass = classes.find(c => c.id === selectedClassForAssign);
    const sectionId = selectedSectionForAssign === 'none' ? null : selectedSectionForAssign;
    const selectedSection = sections.find(s => s.id === sectionId);
    
    try {
      setAssigningClass(true);
      const response = await axios.post(`${API_URL}/api/bulk/students/update-class`, {
        class_id: selectedClassForAssign,
        class_name: selectedClass?.name || '',
        section_id: sectionId || null,
        section_name: selectedSection?.name || null
      }, { headers: getAuthHeader() });
      
      toast.success(`${response.data.updated} students assigned to ${selectedClass?.name}`);
      setAssignClassOpen(false);
      setSelectedClassForAssign('');
      setSelectedSectionForAssign('');
      fetchData(1, classFilter, sectionFilter); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign class');
    } finally {
      setAssigningClass(false);
    }
  };

  // Delete student (only if no payments)
  const handleDeleteStudent = async (student) => {
    try {
      // First check if student can be deleted
      const checkRes = await axios.get(`${API_URL}/api/students/${student.id}/can-delete`, { headers: getAuthHeader() });
      
      if (!checkRes.data.can_delete) {
        toast.error(checkRes.data.reason || 'Cannot delete - student has payment records');
        return;
      }
      
      // Confirm deletion
      if (!window.confirm(`Are you sure you want to DELETE "${student.name}"?\n\nThis will permanently remove:\n- Student record\n- Login credentials\n- Pending invoices\n\nThis action cannot be undone!`)) {
        return;
      }
      
      // Delete student
      await axios.delete(`${API_URL}/api/students/${student.id}`, { headers: getAuthHeader() });
      toast.success(`Student "${student.name}" deleted successfully`);
      fetchData(1, classFilter, sectionFilter); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete student');
    }
  };

  // Fetch student documents
  const fetchStudentDocuments = async (student) => {
    setSelectedStudent(student);
    setDocumentsDialogOpen(true);
    setLoadingDocs(true);
    try {
      const [docsRes, typesRes] = await Promise.all([
        axios.get(`${API_URL}/api/documents/student/${student.id}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/documents/types`, { headers: getAuthHeader() })
      ]);
      setStudentDocuments(docsRes.data.documents || []);
      setMissingDocuments(docsRes.data.missing_required || []);
      setDocumentTypes(typesRes.data.student || {});
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
      setStudentDocuments([]);
      setMissingDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Upload document
  const handleDocumentUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file || !selectedStudent) return;
    
    // File size validation - Photos: 100 KB, Documents: 300 KB
    const isPhoto = docType.includes('photo');
    const maxSize = isPhoto ? 100 * 1024 : 300 * 1024;
    const maxSizeLabel = isPhoto ? '100 KB' : '300 KB';
    
    if (file.size > maxSize) {
      toast.error(`File size too large! Maximum: ${maxSizeLabel}. Your file: ${(file.size / 1024).toFixed(1)} KB`);
      e.target.value = '';
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', docType);
    
    try {
      await axios.post(`${API_URL}/api/documents/upload/${selectedStudent.id}`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded!');
      fetchStudentDocuments(selectedStudent);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Download document
  const downloadDocument = async (docId, filename) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/download/${docId}`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  // Delete document
  const deleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`, { headers: getAuthHeader() });
      toast.success('Document deleted');
      fetchStudentDocuments(selectedStudent);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Open status change dialog
  const openStatusDialog = (student, action) => {
    setStatusStudent(student);
    if (action === 'deactivate') {
      setNewStatus('withdrawn');
    } else if (action === 'activate') {
      setNewStatus('active');
    }
    setStatusReason('');
    setStatusDialogOpen(true);
  };

  // Update student status
  const updateStudentStatus = async () => {
    if (!statusStudent || !newStatus) return;
    
    setStatusLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/students/${statusStudent.id}/status?status=${newStatus}&reason=${encodeURIComponent(statusReason)}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`Student ${newStatus === 'active' ? 'activated' : 'withdrawn'} successfully`);
      setStatusDialogOpen(false);
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const filteredStudents = students.filter(student => {
    // Search filter
    const matchesSearch = student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (student.student_id && student.student_id.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (student.roll_number && student.roll_number.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Section filter (class filter is handled by API)
    const matchesSection = sectionFilter === 'all' || student.section_id === sectionFilter;
    
    return matchesSearch && matchesSection;
  });

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls?.name || '-';
  };

  const getSectionName = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    return section?.name || '-';
  };

  return (
    <DashboardLayout title={isTeacher ? "My Class Students" : "Students"}>
      <div className="space-y-6">
        {/* Teacher Notice */}
        {isTeacher && (
          <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                <Info className="h-4 w-4" />
                <span className="text-sm">You can only view students from your assigned classes. Contact admin for full access.</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Warning for students without class */}
        {studentsWithoutClass.length > 0 && isAdmin && (
          <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-medium">{studentsWithoutClass.length} students have no class assigned!</span>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setAssignClassOpen(true)}
                  className="border-amber-500 text-amber-700 hover:bg-amber-100"
                >
                  Assign Class
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{isTeacher ? 'My Class Students' : 'Total Students'}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalStudents || students.length}</div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {/* Search */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, ID, roll..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-students-input"
                className="pl-10"
              />
            </div>
            {/* Class Filter */}
            <Select value={classFilter} onValueChange={(value) => { setClassFilter(value); setSectionFilter('all'); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="class-filter">
                <SelectValue placeholder="All Classes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                {classes.map(cls => (
                  <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Section Filter */}
            <Select value={sectionFilter} onValueChange={setSectionFilter}>
              <SelectTrigger className="w-full sm:w-40" data-testid="section-filter">
                <SelectValue placeholder="All Sections" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sections</SelectItem>
                {sections
                  .filter(s => classFilter === 'all' || s.class_id === classFilter)
                  .map(section => (
                    <SelectItem key={section.id} value={section.id}>{section.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          {/* Only show add buttons for admin */}
          {isAdmin && (
            <div className="flex gap-2">
              <Button variant="default" onClick={() => navigate('/admission/new')} data-testid="new-admission-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                New Admission
              </Button>
              <Button variant="outline" onClick={() => setBulkImportOpen(true)} data-testid="bulk-import-btn">
                <Upload className="h-4 w-4 mr-2" />
                Bulk Import
              </Button>
          </div>
          )}
        </div>

        {/* Students List */}
        <Card data-testid="students-list-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>All Students</CardTitle>
              <CardDescription>
                Showing {filteredStudents.length} of {totalStudents || students.length} students
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              onClick={async () => {
                try {
                  const response = await axios.get(`${API_URL}/api/students/export/excel`, {
                    headers: getAuthHeader(),
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `students_${new Date().toISOString().slice(0,10)}.xlsx`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Students exported successfully!');
                } catch (error) {
                  toast.error('Export failed: ' + (error.response?.data?.detail || error.message));
                }
              }}
              data-testid="export-students-excel"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </CardHeader>
          <CardContent>
            {filteredStudents.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                <p className="text-muted-foreground">
                  {isTeacher ? 'No students in your assigned classes' : 'No students found'}
                </p>
                {isAdmin && (
                  <Button variant="outline" className="mt-4" onClick={() => setBulkImportOpen(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Import Students from CSV
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left py-3 px-4 font-medium text-sm">Student ID</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Name</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Class</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Email</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Phone</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map(student => {
                      const isInactive = student.status === 'inactive' || student.status === 'withdrawn' || student.status === 'transferred';
                      return (
                      <tr key={student.id} data-testid={`student-row-${student.id}`} className={`border-b hover:bg-muted/50 transition-colors ${isInactive ? 'opacity-60 bg-muted/30' : ''}`}>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="font-mono">
                            {student.student_id || student.roll_number}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 font-medium">{student.name}</td>
                        <td className="py-3 px-4 text-sm">
                          {getClassName(student.class_id)}
                          {student.section_id && ` - ${getSectionName(student.section_id)}`}
                        </td>
                        <td className="py-3 px-4 text-sm">{student.email || '-'}</td>
                        <td className="py-3 px-4 text-sm">{student.phone || '-'}</td>
                        <td className="py-3 px-4">
                          <Badge variant={student.status === 'active' || !student.status ? 'default' : 'destructive'}>
                            {student.status || 'active'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`student-actions-${student.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {isAdmin && (
                                <DropdownMenuItem onClick={() => navigate(`/admission/edit/${student.id}`)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Student
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => openStudentViewDetails(student)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => fetchStudentDocuments(student)}>
                                <FileText className="h-4 w-4 mr-2" />
                                View Documents
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/id-cards?student=${student.id}`)}>
                                <CreditCard className="h-4 w-4 mr-2" />
                                View ID Card
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/student-ledger/${student.id}`)}>
                                <Receipt className="h-4 w-4 mr-2" />
                                View Fee Ledger
                              </DropdownMenuItem>
                              {isAdmin && (
                                <>
                                  <DropdownMenuSeparator />
                                  {(!student.status || student.status === 'active') ? (
                                    <DropdownMenuItem 
                                      onClick={() => openStatusDialog(student, 'deactivate')}
                                      className="text-amber-600 focus:text-amber-600"
                                    >
                                      <UserX className="h-4 w-4 mr-2" />
                                      Withdraw / TC
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem 
                                      onClick={() => openStatusDialog(student, 'activate')}
                                      className="text-green-600 focus:text-green-600"
                                    >
                                      <UserCheck className="h-4 w-4 mr-2" />
                                      Reactivate
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteStudent(student)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Student
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Page {currentPage} of {pagination.total_pages} ({pagination.total} total students)
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchData(currentPage - 1, classFilter, sectionFilter)}
                    disabled={!pagination.has_prev}
                  >
                    Previous
                  </Button>
                  <span className="text-sm px-2">
                    Page {currentPage}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchData(currentPage + 1, classFilter, sectionFilter)}
                    disabled={!pagination.has_next}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Change Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {newStatus === 'active' ? 'Reactivate Student' : 'Withdraw Student'}
              </DialogTitle>
              <DialogDescription>
                {newStatus === 'active' 
                  ? `Are you sure you want to reactivate ${statusStudent?.name}?`
                  : `Are you sure you want to withdraw ${statusStudent?.name}? Their login will be blocked and fee generation will stop.`
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {newStatus !== 'active' && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="withdrawn">Withdrawn</SelectItem>
                      <SelectItem value="transferred">Transferred (TC)</SelectItem>
                      <SelectItem value="passed_out">Passed Out</SelectItem>
                      <SelectItem value="inactive">Inactive (Temporary)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label>Reason (Optional)</Label>
                <Textarea 
                  placeholder="Enter reason for status change..."
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={updateStudentStatus} 
                disabled={statusLoading}
                variant={newStatus === 'active' ? 'default' : 'destructive'}
              >
                {statusLoading ? 'Updating...' : (newStatus === 'active' ? 'Reactivate' : 'Withdraw')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog - Admin only */}
        {isAdmin && (
          <BulkImportDialog
            open={bulkImportOpen}
            onOpenChange={setBulkImportOpen}
            type="students"
            onSuccess={() => fetchData(1, classFilter, sectionFilter)}
          />
        )}

        {/* Documents Viewer Dialog */}
        <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Documents - {selectedStudent?.name}
              </DialogTitle>
            </DialogHeader>
            
            {loadingDocs ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">Loading documents...</p>
              </div>
            ) : (
              <Tabs defaultValue="uploaded" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="uploaded">
                    Uploaded ({studentDocuments.length})
                  </TabsTrigger>
                  <TabsTrigger value="upload">
                    Upload New
                  </TabsTrigger>
                  <TabsTrigger value="missing">
                    Missing ({missingDocuments.length})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="uploaded" className="space-y-3 mt-4">
                  {studentDocuments.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">No documents uploaded yet</p>
                    </div>
                  ) : (
                    studentDocuments.map(doc => (
                      <div 
                        key={doc.id} 
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <FileText className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{doc.document_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                            <p className="text-xs text-muted-foreground">{doc.original_filename}</p>
                            <p className="text-xs text-muted-foreground">
                              Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => downloadDocument(doc.id, doc.original_filename)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={() => deleteDocument(doc.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </TabsContent>

                {/* Upload New Tab */}
                <TabsContent value="upload" className="mt-4">
                  <div className="space-y-3">
                    {Object.entries(documentTypes).map(([key, info]) => {
                      const isPhoto = key.includes('photo');
                      const uploaded = studentDocuments.find(d => d.document_type === key);
                      return (
                        <div key={key} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${uploaded ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'}`}>
                              {uploaded ? (
                                <CheckCircle className="h-5 w-5 text-green-600" />
                              ) : (
                                <Upload className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-sm">{info.label}</p>
                              <p className="text-xs text-muted-foreground">
                                Max: {isPhoto ? '100 KB' : '300 KB'} | {info.required ? 'Required' : 'Optional'}
                              </p>
                              {uploaded && (
                                <p className="text-xs text-green-600">✓ Uploaded</p>
                              )}
                            </div>
                          </div>
                          <div>
                            <input
                              type="file"
                              id={`upload-${key}`}
                              className="hidden"
                              accept={isPhoto ? 'image/*' : '.pdf,.jpg,.jpeg,.png'}
                              onChange={(e) => handleDocumentUpload(e, key)}
                              disabled={uploading}
                            />
                            <Button
                              variant={uploaded ? 'outline' : 'default'}
                              size="sm"
                              onClick={() => document.getElementById(`upload-${key}`)?.click()}
                              disabled={uploading}
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              {uploaded ? 'Replace' : 'Upload'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    {Object.keys(documentTypes).length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        No document types configured
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="missing" className="space-y-3 mt-4">
                  {missingDocuments.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-3" />
                      <p className="text-green-600 font-medium">All required documents uploaded!</p>
                    </div>
                  ) : (
                    missingDocuments.map((doc, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-3 border border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-orange-100 dark:bg-orange-900/50 rounded-lg">
                            <XCircle className="h-5 w-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-orange-700 dark:text-orange-400">{doc.label}</p>
                            <p className="text-xs text-orange-600 dark:text-orange-500">Required document - Not uploaded</p>
                          </div>
                        </div>
                        <input
                          type="file"
                          id={`missing-upload-${doc.key}`}
                          className="hidden"
                          accept={doc.key?.includes('photo') ? 'image/*' : '.pdf,.jpg,.jpeg,.png'}
                          onChange={(e) => handleDocumentUpload(e, doc.key)}
                          disabled={uploading}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById(`missing-upload-${doc.key}`)?.click()}
                          disabled={uploading}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Upload
                        </Button>
                      </div>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* View Student Details Dialog */}
        <Dialog open={viewDetailsOpen} onOpenChange={setViewDetailsOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Student Details - {viewingStudent?.name}
              </DialogTitle>
            </DialogHeader>
            
            {viewingStudent && (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="flex items-start gap-4">
                  <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center overflow-hidden">
                    {studentPhotoUrl ? (
                      <img src={studentPhotoUrl} alt={viewingStudent.name} className="w-full h-full object-cover" />
                    ) : (
                      <GraduationCap className="h-12 w-12 text-primary" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">{viewingStudent.name}</h3>
                    <Badge variant="outline" className="font-mono">{viewingStudent.student_id}</Badge>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Users className="h-3 w-3" /> Class {viewingStudent.class_name}{viewingStudent.section_name && ` - Section ${viewingStudent.section_name}`}</span>
                      <span className="flex items-center gap-1">Roll: {viewingStudent.roll_number || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Personal Details */}
                <div className="grid md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Gender:</span><span>{viewingStudent.gender || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Date of Birth:</span><span>{viewingStudent.date_of_birth || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Blood Group:</span><span>{viewingStudent.blood_group || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Religion:</span><span>{viewingStudent.religion || '-'}</span></div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Category:</span><span>{viewingStudent.category || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Aadhaar:</span><span>{viewingStudent.aadhaar_number || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Admission Date:</span><span>{viewingStudent.admission_date || '-'}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Status:</span><Badge variant={viewingStudent.status === 'active' ? 'default' : 'secondary'}>{viewingStudent.status}</Badge></div>
                  </div>
                </div>

                {/* Parent/Guardian Info */}
                <div className="space-y-3 p-4 border rounded-lg">
                  <h4 className="font-medium text-sm flex items-center gap-2"><Users className="h-4 w-4" /> Parent/Guardian Details</h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Father's Name</p>
                      <p className="font-medium">{viewingStudent.father_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Mother's Name</p>
                      <p className="font-medium">{viewingStudent.mother_name || '-'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{viewingStudent.parent_phone || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{viewingStudent.parent_email || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Address */}
                {viewingStudent.address && (
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium text-sm flex items-center gap-2 mb-2"><MapPin className="h-4 w-4" /> Address</h4>
                    <p className="text-sm">{viewingStudent.address}</p>
                  </div>
                )}

                {/* Fee Status */}
                {viewingStudent.fee_status && (
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <span className="text-sm">Fee Status</span>
                    <Badge variant={viewingStudent.fee_status === 'paid' ? 'default' : viewingStudent.fee_status === 'partial' ? 'secondary' : 'destructive'}>
                      {viewingStudent.fee_status}
                    </Badge>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  {isAdmin && (
                    <Button variant="outline" className="flex-1" onClick={() => { setViewDetailsOpen(false); navigate(`/admission/edit/${viewingStudent.id}`); }}>
                      <Edit className="h-4 w-4 mr-2" /> Edit Student
                    </Button>
                  )}
                  <Button variant="outline" className="flex-1" onClick={() => { setViewDetailsOpen(false); fetchStudentDocuments(viewingStudent); }}>
                    <FileText className="h-4 w-4 mr-2" /> Documents
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => navigate(`/id-cards?student=${viewingStudent.id}`)}>
                    <CreditCard className="h-4 w-4 mr-2" /> ID Card
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Assign Class Dialog */}
        <Dialog open={assignClassOpen} onOpenChange={setAssignClassOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertCircle className="h-5 w-5" />
                Assign Class to Students
              </DialogTitle>
              <DialogDescription>
                {studentsWithoutClass.length} students have no class assigned. Select a class to assign them.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Select Class *</Label>
                <Select value={selectedClassForAssign} onValueChange={setSelectedClassForAssign}>
                  <SelectTrigger data-testid="select-class-assign">
                    <SelectValue placeholder="Choose class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Select Section (Optional)</Label>
                <Select value={selectedSectionForAssign} onValueChange={setSelectedSectionForAssign}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose section" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Section</SelectItem>
                    {sections.filter(s => s.class_id === selectedClassForAssign).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg text-sm">
                <p className="font-medium text-amber-700">Students to be updated:</p>
                <ul className="mt-2 max-h-32 overflow-y-auto space-y-1 text-amber-600">
                  {studentsWithoutClass.slice(0, 10).map(s => (
                    <li key={s.id}>• {s.name} ({s.student_id})</li>
                  ))}
                  {studentsWithoutClass.length > 10 && (
                    <li className="font-medium">...and {studentsWithoutClass.length - 10} more</li>
                  )}
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignClassOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleBulkAssignClass} 
                disabled={assigningClass || !selectedClassForAssign}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {assigningClass ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Assign to {studentsWithoutClass.length} Students
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default StudentsPage;
